import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAtom, useSetAtom } from 'jotai';
import useInfiniteQuery from '../../Hooks/useInfiniteQuery';
import { IconButton } from '@mui/material';
import useAPI from '../../api';
import SectionPage from '../../Components/Paper/SectionPage';
import mapFilters from '../../Helpers/Filters/mapFilters';
import { openSnackbarAdviseAtom, searchValuesAtom } from '../../Atoms';
import CampaignDialog from './Components/CampaignDialog';
import CampaignsFilters from './Components/CampaignsFilters';
import StatusChip from '../../Components/Chip/StatusChip';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { FlexBox, FullSizeFlexBox } from '../../Components/FlexBox';
import { DeleteRounded, EditRounded } from '@mui/icons-material';
import ConfirmDialog from '../../Components/Dialog/ConfirmDialog';
import { columns } from './Utils/constant';
import { formatPayloadDate } from '../../Helpers/dates';

const renderChip = (data, onEdit, onDelete) => {
  return (
    <FullSizeFlexBox gap="16px">
      <StatusChip
        status={data?.isActive ? 'ACTIVE' : 'INACTIVE'}
        options={{
          ACTIVE: CheckCircleOutlineRoundedIcon,
          INACTIVE: HighlightOffRoundedIcon,
        }}
        colors={{ ACTIVE: '#34A853', INACTIVE: '#b00020' }}
      />
      <FlexBox>
        <IconButton
          id="edit-campaign-button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(data);
          }}
        >
          <EditRounded />
        </IconButton>
        <IconButton
          id="delete-campaign-button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(data);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </FlexBox>
    </FullSizeFlexBox>
  );
};

const normalizeDateField = (value) => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return formatPayloadDate(value)?.toISOString();
  }

  return value;
};

const normalizeCampaignDates = (payload) => {
  if (!payload) return payload;

  return {
    ...payload,
    startDate: normalizeDateField(payload.startDate),
    endDate: normalizeDateField(payload.endDate),
  };
};

const CampaignsManagement = function CampaignsManagement() {
  const intl = useIntl();
  const API = useAPI();

  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchValues, setSearchValues] = useAtom(searchValuesAtom);
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);

  const handleOpenCreateDialog = () => {
    setCreateDialog(true);
  };
  const handleCloseCreateDialog = () => {
    setCreateDialog(false);
  };
  const handleOpenEditDialog = (data) => {
    setEditDialog(data);
  };
  const handleCloseEditDialog = () => {
    setEditDialog(false);
  };

  const handleChangeFilters = ({ isActive, startDate, endDate }) => {
    setSearchValues((prev) => ({
      ...prev,
      isActive,
      startDate: normalizeDateField(startDate),
      endDate: normalizeDateField(endDate),
    }));
  };

  useEffect(() => {
    setSearchValues((prev) => ({ ...prev, isActive: true }));
  }, []);

  const getCampaignsHistory = useCallback(
    (pageNumber) =>
      API.getCommercialCampaignsHistory(
        {
          pageNumber,
          silent: true,
          filterExpression: mapFilters(
            ['description'],
            [
              {
                keyName: 'isActive',
                operator: 'eq',
              },
              {
                keyName: 'startDate',
                operator: 'gt',
              },
              {
                keyName: 'endDate',
                operator: 'lt',
              },
            ],
            [],
            searchValues
          ),
          sortExpression: searchValues?.sortValue
            ? {
                sortValue: searchValues?.sortValue.value,
                sort: searchValues?.sortValue.direction,
              }
            : null,
        },
        () => {}
      ),
    [searchValues]
  );
  const { ObserverWrapper, reload } = useInfiniteQuery(getCampaignsHistory);

  const handleCreateCampaign = (CommercialCampaignData) => {
    const payload = normalizeCampaignDates(CommercialCampaignData);

    API.createCommercialCampaign(
      payload,
      () => {
        reload();
        handleCloseCreateDialog();
        openSnackbar({
          message: intl.messages.CAMPAIGN_CREATED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_CREATING_CAMPAIGN,
          severity: 'error',
        });
      }
    );
  };

  const handleOpenConfirmDelete = (data) => {
    setConfirmDelete(data?.id);
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDelete(null);
  };
  const handleDeleteCampaign = (campaignId) => {
    API.deleteCommercialCampaign(
      campaignId,
      () => {
        reload();
        handleCloseConfirmDelete();
        openSnackbar({
          message: intl.messages.CAMPAIGN_DELETED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_DELETING_CAMPAIGN,
          severity: 'error',
        });
      }
    );
  };

  const handleEditCampaign = (CommercialCampaignData) => {
    const payload = normalizeCampaignDates(CommercialCampaignData);

    API.updateCommercialCampaign(
      payload,
      () => {
        reload();
        handleCloseEditDialog();
        openSnackbar({
          message: intl.messages.CAMPAIGN_EDITED_SUCCESSFULLY,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR_EDITING_CAMPAIGN,
          severity: 'error',
        });
      }
    );
  };

  return (
    <>
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="DELETE_CAMPAIGN"
        question="DELETE_CAMPAIGN_QUESTION"
        onCancel={handleCloseConfirmDelete}
        onSubmit={() => handleDeleteCampaign(confirmDelete)}
      />
      <CampaignDialog
        open={createDialog}
        onClose={handleCloseCreateDialog}
        onSubmit={handleCreateCampaign}
      />
      <CampaignDialog
        open={Boolean(editDialog)}
        onClose={handleCloseEditDialog}
        defaultData={editDialog}
        edit
        onSubmit={handleEditCampaign}
      />
      <SectionPage
        disableClickRow
        onClickFab={handleOpenCreateDialog}
        sortingOptions={[
          { label: 'STARTDATE', value: 'startDate', isDefault: true },
          { label: 'DESCRIPTION', value: 'description' },
          { label: 'ENDDATE', value: 'endDate' },
        ]}
        hideExportExcel
        sectionName="CAMPAIGNS_MANAGEMENT"
        fab={intl.messages.NEW_CAMPAIGN}
        fabVariant="extended"
        columns={columns}
        customStatusChipCallback={({ data }) =>
          renderChip(data, handleOpenEditDialog, handleOpenConfirmDelete)
        }
        ListObserverWrapper={ObserverWrapper}
        customFilters={
          <CampaignsFilters
            onConfirm={handleChangeFilters}
            defaultValue={searchValues}
          />
        }
      />
    </>
  );
};

export default CampaignsManagement;
